import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select'
import { PanelComponent } from '@components/core/panel/panel.component';
import { EvaluationService } from '@services/core/evaluation.service';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import Swal from 'sweetalert2';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminQuestionsComponent } from './admin-questions.component';
import { canLoadCategories, confirmRemoveCategory, firstCategoryId, pickCategoryId, pickGroup, pickModule, upsertCategoryRow } from './admin-evaluation-helpers';
import { withCategoryScores } from './admin-evaluation-score.helpers';
import { downloadEvaluationPdf, groupLabel, moduleName, pdfErrorText, previewCategories } from './admin-evaluation-pdf.utils';
import { printEvaluationForm } from './admin-evaluation-print.utils';

@Component({
  selector: 'app-admin-evaluation',
  templateUrl: './admin-evaluation.component.html',
  styleUrls: ["./admin-evaluation.component.scss"],
  imports: [PanelComponent, FormsModule, ReactiveFormsModule, NgbTooltipModule, NgSelectComponent, AdminQuestionsComponent],
  providers: [EvaluationService],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
})
export class AdminEvaluationComponent implements OnInit {
  protected modules
  protected selectedModule
  protected groups
  protected selectedGroup
  protected categories
  protected questions
  protected selectCategoryId
  protected showPreview = false
  protected preview
  protected yearSelected

  private evaluationService = inject(EvaluationService)
  private modalService = inject(NgbModal)
  private fb = inject(FormBuilder)
  private route = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private modalOptions = { size: 'xl', scrollable: true, animation: true, backdrop: null }

  protected years = []

  protected newCategoryForm = this.fb.group({
    id: [null],
    moduleId: ["", Validators.required],
    groupId: ["", Validators.required],
    year: [""],
    name: ["", Validators.required],
    tooltip: [""],
    enabled: [true]
  })

  ngOnInit(): void {
    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => this.initFromData(data['templateData']));
  }

  private initFromData(data: any) {
    this.resetEvaluationState();
    this.applyTemplateData(data);
    this.yearSelected = new Date().getFullYear();
    this.setYears(2024);
  }

  /** Limpia estado de evaluación. */
  private resetEvaluationState() {
    this.categories = [];
    this.questions = [];
    this.selectCategoryId = null;
    this.showPreview = false;
    this.preview = null;
  }

  /** Aplica datos de plantilla. */
  private applyTemplateData(data: any) {
    this.modules = data.modules;
    this.selectedModule = pickModule(data);
    this.groups = data.groups;
    this.selectedGroup = pickGroup(data);
    this.categories = data.categories;
    this.selectCategoryId = pickCategoryId(data);
    this.questions = data.questions;
  }

  setYears(initial: number) {
    const year = new Date().getFullYear();
    this.yearSelected = year;
    this.years = [];
    for (let i = year; i >= initial; i--) this.years.push(i);
  }

  open(modal) {
    this.modalService.open(modal, this.modalOptions).result.then(() => {}, () => {});
  }

  getCategories() {
    if (canLoadCategories(this.selectedModule, this.selectedGroup) === false) return;
    this.evaluationService.getCategories(this.selectedModule.id, this.yearSelected, this.selectedGroup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => this.onCategories(res));
  }

  /** Guarda categorías y carga preguntas. */
  private onCategories(res: any) {
    this.categories = res;
    this.selectCategoryId = firstCategoryId(res);
    this.getQuestions();
  }

  getQuestions() {
    this.showPreview = false;
    if (this.selectCategoryId) this.fetchQuestions();
  }

  /** Carga preguntas de la categoría. */
  private fetchQuestions() {
    this.evaluationService.getQuestions(this.selectCategoryId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => this.onQuestions(res));
  }

  /** Guarda preguntas recibidas. */
  private onQuestions(res: any) {
    this.questions = res;
  }

  editCategory(modal, category) {
    this.newCategoryForm.patchValue(category);
    this.newCategoryForm.patchValue({ moduleId: this.selectedModule?.id, groupId: this.selectedGroup?.id });
    this.open(modal);
  }

  addCategory() {
    this.newCategoryForm.patchValue({
      year: this.yearSelected,
      moduleId: this.selectedModule?.id,
      groupId: this.selectedGroup?.id,
      enabled: true,
    });
    this.upsertCategory(this.newCategoryForm.value);
    this.evaluationService.updateCategory(this.categories)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { this.getCategories(); this.newCategoryForm.reset(); });
  }

  private upsertCategory(formValue: any) {
    upsertCategoryRow(this.categories, formValue);
  }

  selectCategory(id) {
    this.selectCategoryId = id;
    this.getQuestions();
  }

  moveUp(i) {
    moveItemInArray(this.categories, i, i - 1);
    this.evaluationService.updateCategory(this.categories)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getCategories());
  }

  moveDown(i) {
    moveItemInArray(this.categories, i, i + 1);
    this.evaluationService.updateCategory(this.categories)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getCategories());
  }

  preventRemoveCategory(id) {
    confirmRemoveCategory(() => this.removeCategory(id));
  }

  removeCategory(id) {
    this.evaluationService.deleteCategory(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.getCategories());
  }

  viewPreview() {
    this.showPreview = true;
    this.evaluationService.getPreview(this.selectedModule.id, this.selectedGroup, this.yearSelected)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => { this.preview = res; this.applyPreviewScores(); });
  }

  /** Agrega el puntaje total a cada categoría del preview. */
  private applyPreviewScores() {
    this.preview.categories = withCategoryScores(this.preview?.categories);
  }

  exportPdf() {
    printEvaluationForm(this.pdfMeta(), previewCategories(this.preview));
  }

  /** Descarga el PDF vía jspdf (requiere pnpm add jspdf jspdf-autotable). */
  downloadPdf() {
    downloadEvaluationPdf(this.pdfMeta(), previewCategories(this.preview)).then(() => {
      Swal.fire({ icon: 'success', title: 'PDF generado', timer: 1500, showConfirmButton: false });
    }, (err) => {
      Swal.fire({ icon: 'error', title: 'Error', text: pdfErrorText(err) });
    });
  }

  /** Arma los metadatos del PDF. */
  private pdfMeta() {
    return { module: moduleName(this.selectedModule), group: groupLabel(this.selectedGroup), year: this.yearSelected };
  }
}
